// useAuth - Unified authentication hook combining NextAuth and Zustand
'use client'

import { useEffect, useState, useCallback } from 'react'
import { useSession, signIn as nextAuthSignIn, signOut as nextAuthSignOut } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import { useAppStore } from '@/store/useAppStore'
import AuthService from '@/services/AuthService'
import { appConfig } from '@/configs/app.config'
import { UserRole } from '@/constants/roles.constant'
import type { SignInCredential } from '@/@types/auth'
import type { User } from '@/store/useAppStore'

export interface UseAuthReturn {
  user: User | null
  role: UserRole | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: SignInCredential) => Promise<UserRole | null>
  logout: () => Promise<void>
  updateProfile: (data: Partial<User>) => void
}

export function useAuth(): UseAuthReturn {
  const router = useRouter()
  const { data: session, status } = useSession()
  const [isLoading, setIsLoading] = useState(false)

  const {
    user,
    accessToken,
    isAuthenticated,
    setCredentials,
    signOutSuccess,
    syncFromSession,
    updateUser,
    clearGlobalError,
  } = useAppStore()

  // Sync Zustand store with NextAuth session on mount and session changes
  useEffect(() => {
    if (status === 'authenticated' && session) {
      syncFromSession(session)
      // Clear any stale global errors on successful authentication
      clearGlobalError()
    } else if (status === 'unauthenticated') {
      syncFromSession(null)
    }
  }, [session, status, syncFromSession])

  const login = useCallback(
    async (credentials: SignInCredential): Promise<UserRole | null> => {
      setIsLoading(true)
      try {
        // Use NextAuth signIn
        const result = await nextAuthSignIn('credentials', {
          email: credentials.email,
          password: credentials.password,
          redirect: false,
        })

        if (result?.error) {
          throw new Error(result.error)
        }

        if (result?.ok) {
          // Clear any existing errors on successful login
          clearGlobalError()
          
          // NextAuth will update the session, which will trigger our useEffect
          // to sync the Zustand store. We need to wait a bit for the session to update.
          await new Promise((resolve) => setTimeout(resolve, 100))

          // Get the updated session to extract role
          const response = await fetch('/api/auth/session')
          const sessionData = await response.json()

          if (sessionData?.user?.role) {
            return sessionData.user.role as UserRole
          }
        }

        return null
      } catch (error) {
        console.error('[useAuth] Login error:', error)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const logout = useCallback(async (): Promise<void> => {
    // Prevent multiple simultaneous logout calls
    if (isLoading) {
      console.log('[useAuth] Logout already in progress, ignoring duplicate call')
      return
    }

    setIsLoading(true)
    try {
      console.log('[useAuth] Starting logout process...')
      
      // Call backend logout endpoint (optional, for token invalidation)
      try {
        await AuthService.signOut()
        console.log('[useAuth] Backend logout successful')
      } catch (error) {
        console.warn('[useAuth] Backend logout failed:', error)
        // Continue with logout even if backend call fails
      }

      // Clear Zustand store first
      signOutSuccess()
      console.log('[useAuth] Zustand store cleared')

      // Sign out from NextAuth
      await nextAuthSignOut({ redirect: false })
      console.log('[useAuth] NextAuth signout complete')

      // Redirect to login
      router.push(`${appConfig.unAuthenticatedEntryPath}?session=ended`)
    } catch (error) {
      console.error('[useAuth] Logout error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [router, signOutSuccess, isLoading])

  const updateProfile = useCallback(
    (data: Partial<User>) => {
      updateUser(data)
    },
    [updateUser]
  )

  return {
    user,
    role: user?.role || null,
    token: accessToken,
    isAuthenticated: status === 'authenticated' && isAuthenticated,
    isLoading: status === 'loading' || isLoading,
    login,
    logout,
    updateProfile,
  }
}
